import React, { useState } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight,
  ArrowUpRight,
  Building2,
  Cog,
  Copy,
  MapPin,
  Mail,
  Play,
  Sparkles,
  Layers,
  Users,
  TrendingUp,
  X,
} from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1766246092562-10a41c2a726d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwaHViJTIwaW5kb29yfGVufDB8fHx8MTc3NzA1MTE5OHww&ixlib=rb-4.1.0&q=85",
  urbanSport:
    "https://customer-assets.emergentagent.com/job_indoor-quartiere/artifacts/cma9tshx_POST%20ALEXQUARE-2.png",
  piazza:
    "https://images.pexels.com/photos/462341/pexels-photo-462341.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  food: "https://images.unsplash.com/photo-1758448500813-59f3770da00d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBmb29kJTIwY291cnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzcwNTExOTN8MA&ixlib=rb-4.1.0&q=85",
  study:
    "https://images.pexels.com/photos/7212924/pexels-photo-7212924.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  gaming:
    "https://images.pexels.com/photos/7862518/pexels-photo-7862518.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  events:
    "https://images.unsplash.com/photo-1767274101063-a735a6849afc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHx1cmJhbiUyMGNvbW11bml0eSUyMGV2ZW50JTIwY3Jvd2R8ZW58MHx8fHwxNzc3MDUxMTkzfDA&ixlib=rb-4.1.0&q=85",
  retail:
    "https://images.pexels.com/photos/4914811/pexels-photo-4914811.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  services:
    "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&w=1600&q=80",
};

const pillars = [
  {
    icon: Users,
    label: "01 — Audience",
    title: "Nuove generazioni",
    text: "Uno spazio pensato per i giovani, una fascia priva di luoghi urbani progettati specificamente per incontro, sport, creatività e appartenenza.",
  },
  {
    icon: Layers,
    label: "02 — Format",
    title: "Spazio multifunzionale",
    text: "Un ambiente che integra urban sport, food, eventi, studio, gaming, creatività, retail e servizi in un unico luogo riconoscibile e vivo.",
  },
  {
    icon: TrendingUp,
    label: "03 — Scala",
    title: "Format replicabile",
    text: "Un modello urbano pensato per essere validato attraverso il progetto pilota e successivamente adattato ad altri territori con caratteristiche simili.",
  },
];

const features = [
  {
    title: "Urban Sport Park",
    img: IMG.urbanSport,
    span: "md:col-span-7 md:row-span-2",
    tag: "Movement",
  },
  {
    title: "Piazza Centrale",
    img: IMG.piazza,
    span: "md:col-span-5",
    tag: "Connect",
  },
  {
    title: "Food & Social Area",
    img: IMG.food,
    span: "md:col-span-5",
    tag: "Taste",
  },
  {
    title: "Studio & Smart Work",
    img: IMG.study,
    span: "md:col-span-6",
    tag: "Focus",
  },
  {
    title: "Gaming & Creatività",
    img: IMG.gaming,
    span: "md:col-span-6",
    tag: "Play",
  },
  {
    title: "Eventi & Community",
    img: IMG.events,
    span: "md:col-span-7",
    tag: "Live",
  },
  {
    title: "Retail Streetwear",
    img: IMG.retail,
    span: "md:col-span-5",
    tag: "Culture",
  },
  {
    title: "Spazio Musica",
    img: IMG.services,
    span: "md:col-span-12",
    tag: "Sound",
  },
];

const numbers = [
  { value: "8.000", unit: "mq", label: "Area complessiva prevista" },
  { value: "3.400", unit: "mq", label: "Superficie coperta" },
  { value: "55.000", unit: "young", label: "Giovani 12-25 anni entro 20 km" },
  { value: "140.000", unit: "presenze/anno", label: "Presenze annue stimate" },
];

const partnerBlocks = [
  {
    icon: Building2,
    kicker: "01",
    title: "Componente immobiliare",
    text: "Lo sviluppatore realizza l'immobile e valorizza un asset dedicato a funzioni leisure, sociali e urbane, con un modello orientato alla locazione degli spazi alla società gestionale.",
  },
  {
    icon: Cog,
    kicker: "02",
    title: "Componente gestionale",
    text: "L'operatore gestisce attività sportive, food & beverage, eventi, retail e servizi, costruendo una community stabile e un utilizzo continuativo della struttura.",
  },
  {
    icon: Copy,
    kicker: "03",
    title: "Replicabilità",
    text: "Dopo la validazione del progetto pilota, il format può essere adattato ad altri territori con bacini giovanili significativi, buona accessibilità e disponibilità di aree idonee.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

function HeroHeading() {
  const lines = [
    "Un nuovo modello",
    "di spazio urbano",
    "per i giovani.",
  ];
  return (
    <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-semibold uppercase leading-[0.92] tracking-tighter">
      {lines.map((line, i) => (
        <span key={line} className="block overflow-hidden">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{
              duration: 0.9,
              delay: 0.1 + i * 0.12,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="block"
          >
            {i === 2 ? (
              <span>
                per i{" "}
                <span className="italic font-normal text-[#FF5E1A]">giovani</span>.
              </span>
            ) : (
              line
            )}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

function HeroHeadingCentered() {
  const lines = ["Un nuovo modello", "di spazio urbano", "per i giovani."];
  return (
    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-semibold uppercase leading-[0.95] tracking-tighter">
      {lines.map((line, i) => (
        <span key={line} className="block overflow-hidden">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{
              duration: 0.9,
              delay: 0.35 + i * 0.12,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="block"
          >
            {i === 2 ? (
              <span>
                per i{" "}
                <span className="italic font-normal text-[#FF5E1A]">
                  giovani
                </span>
                .
              </span>
            ) : (
              line
            )}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

export default function AlexquareLandingPage() {
  const { scrollYProgress } = useScroll();
  const heroParallax = useTransform(scrollYProgress, [0, 0.3], [0, -80]);
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 font-body selection:bg-[#FF5E1A] selection:text-zinc-950">
      {/* HEADER */}
      <header
        data-testid="site-header"
        className="fixed top-0 z-50 w-full border-b border-zinc-800/80 bg-zinc-950/70 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
          <a
            href="#top"
            data-testid="logo-link"
            className="flex items-center gap-3 group"
          >
            <div className="leading-tight">
              <p className="font-display text-sm font-semibold tracking-wide uppercase">
                Alexquare
              </p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                Urban Hub
              </p>
            </div>
          </a>

          <nav className="hidden items-center gap-8 md:flex">
            <a
              data-testid="nav-progetto"
              href="#progetto"
              className="text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-zinc-50"
            >
              Progetto
            </a>
            <a
              data-testid="nav-concept"
              href="#concept"
              className="text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-zinc-50"
            >
              Concept
            </a>
            <a
              data-testid="nav-pilota"
              href="#pilota"
              className="text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-zinc-50"
            >
              Pilota
            </a>
            <a
              data-testid="nav-sviluppo"
              href="#sviluppo"
              className="text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-zinc-50"
            >
              Sviluppo
            </a>
          </nav>

          <a
            href="#contatti"
            data-testid="header-contact-btn"
            className="group inline-flex items-center gap-2 bg-[#FF5E1A] px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-zinc-950 transition-colors hover:bg-[#E64A00]"
          >
            Contatti
            <ArrowUpRight
              size={14}
              className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </a>
        </div>
      </header>

      <main id="top">
        {/* HERO */}
        <section className="relative overflow-hidden px-6 pb-24 pt-36 md:pb-32 md:pt-44 lg:px-10">
          {/* Background video */}
          <motion.div
            style={{ y: heroParallax }}
            className="absolute inset-0 -z-10"
          >
            <video
              src="https://customer-assets.emergentagent.com/job_indoor-quartiere/artifacts/tnbxyyip_AXQ%20VIDE0%202026.mp4"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster={IMG.hero}
              className="h-full w-full object-cover opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/60 via-zinc-950/70 to-zinc-950" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,94,26,0.10),transparent_45%)]" />
          </motion.div>

          <div className="relative mx-auto max-w-5xl text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto mb-10 flex justify-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_indoor-quartiere/artifacts/dy7t7nlr_logo%20axq%2023.png"
                alt="Alexquare logo"
                className="h-44 w-44 object-contain drop-shadow-[0_10px_40px_rgba(255,94,26,0.35)] md:h-64 md:w-64 lg:h-80 lg:w-80"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8 inline-flex items-center gap-2 border border-zinc-700 bg-zinc-950/60 px-4 py-2 backdrop-blur"
            >
              <span className="h-2 w-2 bg-[#FF5E1A] animate-pulse" />
              <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-zinc-300">
                Progetto pilota · Tradate (VA)
              </span>
            </motion.div>

            <HeroHeadingCentered />

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-zinc-400 md:text-xl"
            >
              Alexquare è un format indoor multifunzionale dedicato alle nuove
              generazioni: sport urbano, food & beverage, negozi streetwear e
              brand dedicati, socialità, creatività, eventi e servizi in un
              unico ambiente progettato come un quartiere urbano.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4"
            >
              <a
                href="#progetto"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-center gap-2 bg-[#FF5E1A] px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-zinc-950 transition-colors hover:bg-[#E64A00]"
              >
                Scopri il progetto
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="https://customer-assets-agu9un31.emergentagent.net/job_indoor-quartiere/artifacts/9ee9c77cd125efc5_AXQ%20DOSSIER%20SETT%2026.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 border border-zinc-700 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-zinc-50 transition-colors hover:border-zinc-50 hover:bg-zinc-50 hover:text-zinc-950"
              >
                Sfoglia il dossier
              </a>
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                data-testid="hero-video-cta"
                className="group inline-flex items-center justify-center gap-2 border border-zinc-700 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-zinc-50 transition-colors hover:border-[#FF5E1A] hover:text-[#FF5E1A]"
              >
                <Play size={14} className="transition-transform group-hover:scale-110" fill="currentColor" />
                Guarda il video
              </button>
            </motion.div>

            {/* Stat card below */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1 }}
              className="relative mx-auto mt-20 max-w-3xl text-left"
              data-testid="hero-stat-card"
            >
              <div className="relative border border-zinc-800 bg-zinc-900/40 backdrop-blur-sm">
                <div className="border-b border-zinc-800 p-6 md:p-8">
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF5E1A]">
                    / Concept
                  </p>
                  <p className="mt-3 font-display text-3xl font-medium uppercase tracking-tight leading-tight md:text-4xl">
                    Un quartiere indoor
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400 md:text-base">
                    Un luogo stabile, riconoscibile e accessibile dove le
                    attività non sono separate, ma convivono in un ecosistema
                    urbano.
                  </p>
                </div>
                <div className="grid grid-cols-2">
                  {numbers.slice(0, 2).map((n, i) => (
                    <div
                      key={n.label}
                      className={`p-6 md:p-8 ${i === 0 ? "border-r border-zinc-800" : ""}`}
                    >
                      <p className="font-display text-3xl font-semibold tracking-tighter md:text-4xl">
                        {n.value}
                        <span className="text-sm font-normal text-zinc-500 ml-1">
                          {n.unit}
                        </span>
                      </p>
                      <p className="mt-2 text-[11px] uppercase tracking-wider text-zinc-500">
                        {n.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-3 -right-3 -z-10 h-full w-full border border-[#FF5E1A]/40" />
            </motion.div>
          </div>

          {/* Ticker below hero */}
          <div className="relative mt-24 -mx-6 lg:-mx-10">
            <div className="border-y border-zinc-800 bg-zinc-950 py-5">
              <div className="flex items-center gap-8 text-xs uppercase tracking-[0.3em] text-zinc-500">
                <div className="flex items-center gap-2 pl-6 lg:pl-10 shrink-0">
                  <span className="h-1.5 w-1.5 bg-[#FF5E1A]" />
                  <span>Scroll</span>
                </div>
                <Marquee speed={45} gradient={false} pauseOnHover>
                  <span className="mx-6">
                    URBAN HUB · TRADATE · NEXT GEN SPACE · INDOOR DISTRICT ·
                    YOUTH CULTURE · SPORT · FOOD · EVENTS · GAMING · COMMUNITY ·
                    CREATIVITY ·{" "}
                  </span>
                </Marquee>
              </div>
            </div>
          </div>
        </section>

        {/* IL PROGETTO */}
        <section
          id="progetto"
          className="relative border-b border-zinc-800 px-6 py-24 md:py-32 lg:px-10"
        >
          <div className="mx-auto max-w-[1400px]">
            <div className="grid gap-12 md:grid-cols-12">
              <div className="md:col-span-3">
                <p className="sticky top-28 text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF5E1A]">
                  / Il progetto
                </p>
              </div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeUp}
                className="md:col-span-9"
              >
                <h2 className="font-display text-4xl font-medium uppercase leading-[1.02] tracking-tight md:text-6xl">
                  Nelle città mancano spazi pensati per{" "}
                  <span className="text-zinc-500">
                    le nuove generazioni.
                  </span>
                </h2>
                <p className="mt-8 max-w-2xl text-lg leading-relaxed text-zinc-400">
                  Alexquare nasce per rispondere a questo vuoto: creare un polo
                  urbano capace di generare frequentazione, identità e
                  community, unendo funzioni sociali, sportive, creative e
                  commerciali in una struttura indoor ad alto utilizzo.
                </p>
              </motion.div>
            </div>

            <div className="mt-16 grid gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-3">
              {pillars.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.div
                    key={p.title}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    className="group relative bg-zinc-950 p-8 transition-colors hover:bg-zinc-900 md:p-10"
                    data-testid={`pillar-${i}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                        {p.label}
                      </p>
                      <Icon
                        size={22}
                        className="text-zinc-600 transition-colors group-hover:text-[#FF5E1A]"
                      />
                    </div>
                    <h3 className="mt-10 font-display text-2xl font-medium uppercase tracking-tight md:text-3xl">
                      {p.title}
                    </h3>
                    <p className="mt-4 leading-relaxed text-zinc-400">
                      {p.text}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* EDITORIAL MARQUEE */}
        <section className="overflow-hidden border-b border-zinc-800 bg-zinc-950 py-10 md:py-16">
          <Marquee speed={70} gradient={false}>
            <span className="font-display text-[20vw] md:text-[14vw] font-semibold uppercase tracking-tighter leading-none text-stroke-zinc mx-8">
              Alexquare —
            </span>
            <span className="font-display text-[20vw] md:text-[14vw] font-semibold uppercase tracking-tighter leading-none text-[#FF5E1A] mx-8">
              Indoor District
            </span>
            <span className="font-display text-[20vw] md:text-[14vw] font-semibold uppercase tracking-tighter leading-none text-stroke-zinc mx-8">
              Alexquare —
            </span>
            <span className="font-display text-[20vw] md:text-[14vw] font-semibold uppercase tracking-tighter leading-none text-zinc-50 mx-8">
              Next Gen
            </span>
          </Marquee>
        </section>

        {/* CONCEPT / BENTO */}
        <section
          id="concept"
          className="relative border-b border-zinc-800 px-6 py-24 md:py-32 lg:px-10"
        >
          <div className="mx-auto max-w-[1400px]">
            <div className="grid gap-12 md:grid-cols-12">
              <div className="md:col-span-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF5E1A]">
                  / Il concept
                </p>
                <h2 className="mt-6 font-display text-4xl font-medium uppercase leading-[1.02] tracking-tight md:text-5xl lg:text-6xl">
                  Un quartiere indoor,
                  <br />
                  <span className="italic font-normal text-zinc-500">
                    non un semplice centro.
                  </span>
                </h2>
              </div>
              <div className="md:col-span-6 md:col-start-7">
                <p className="text-lg leading-relaxed text-zinc-400">
                  Il cuore del format è la piazza centrale: uno spazio di
                  incontro e socialità che integra anche aree ristoro, street
                  food e negozi dedicati al mondo giovanile. Un vero centro
                  urbano indoor dove sport, relax, shopping e community si
                  incontrano favorendo la permanenza e trasformando Alexquare
                  in un luogo vivo durante l'intera giornata.
                </p>
              </div>
            </div>

            {/* Bento Grid */}
            <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-12 md:auto-rows-[220px]">
              {features.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.6,
                    delay: (i % 4) * 0.08,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className={`group relative overflow-hidden border border-zinc-800 bg-zinc-900 min-h-[280px] md:min-h-0 ${f.span}`}
                  data-testid={`feature-${i}`}
                >
                  <img
                    src={f.img}
                    alt={f.title}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover opacity-55 transition-all duration-700 group-hover:opacity-80 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
                  <div className="relative flex h-full flex-col justify-between p-6 md:p-8">
                    <div className="flex items-start justify-between">
                      <span className="inline-flex items-center gap-2 border border-zinc-50/30 bg-zinc-950/40 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-50 backdrop-blur-sm">
                        {f.tag}
                      </span>
                      <span className="font-display text-xs text-zinc-500">
                        / 0{i + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl font-medium uppercase tracking-tight text-zinc-50 md:text-3xl lg:text-4xl">
                      {f.title}
                    </h3>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PROGETTO PILOTA — LIGHT MODE */}
        <section
          id="pilota"
          className="border-b border-zinc-200 bg-zinc-50 px-6 py-24 text-zinc-950 md:py-32 lg:px-10"
        >
          <div className="mx-auto max-w-[1400px]">
            <div className="grid gap-12 md:grid-cols-12 md:items-end">
              <div className="md:col-span-7">
                <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                  / Progetto pilota
                </p>
                <h2 className="mt-6 font-display text-4xl font-medium uppercase leading-[0.98] tracking-tight md:text-6xl lg:text-7xl">
                  Tradate come prima
                  <br />
                  <span className="text-zinc-400">applicazione</span> del format.
                </h2>
              </div>
              <div className="md:col-span-5">
                <p className="text-lg leading-relaxed text-zinc-600">
                  Il primo Alexquare Urban Hub è previsto a Tradate (VA), su
                  un'area idonea lungo un asse territoriale strategico. Il
                  progetto pilota ha l'obiettivo di testare il modello,
                  validare la gestione e dimostrare la replicabilità del
                  format.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 border border-zinc-300 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-700">
                  <MapPin size={14} />
                  Tradate · Varese · Italia
                </div>
              </div>
            </div>

            {/* Massive Numbers */}
            <div className="mt-20 grid gap-px border border-zinc-200 bg-zinc-200 md:grid-cols-4">
              {numbers.map((n, i) => (
                <motion.div
                  key={n.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                  className="bg-zinc-50 p-8 md:p-10"
                  data-testid={`number-${i}`}
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                    / 0{i + 1}
                  </p>
                  <p className="mt-6 font-display text-6xl font-semibold tracking-tighter text-zinc-950 md:text-7xl lg:text-8xl">
                    {n.value}
                  </p>
                  <p className="mt-2 text-sm font-medium uppercase tracking-wider text-zinc-500">
                    {n.unit}
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-zinc-600">
                    {n.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SVILUPPO / PARTNER */}
        <section
          id="sviluppo"
          className="border-b border-zinc-800 px-6 py-24 md:py-32 lg:px-10"
        >
          <div className="mx-auto max-w-[1400px]">
            <div className="grid gap-12 md:grid-cols-12">
              <div className="md:col-span-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF5E1A]">
                  / Sviluppo
                </p>
                <h2 className="mt-6 font-display text-4xl font-medium uppercase leading-[1.02] tracking-tight md:text-5xl lg:text-6xl">
                  Un modello per partner
                  <br />
                  <span className="text-zinc-500">
                    immobiliari e gestionali.
                  </span>
                </h2>
                <div className="mt-10 hidden md:block">
                  <div className="border border-zinc-800 bg-zinc-900/40 p-6">
                    <Sparkles size={20} className="text-[#FF5E1A]" />
                    <p className="mt-4 text-sm leading-relaxed text-zinc-400">
                      Il format unisce due anime: lo sviluppo immobiliare di un
                      asset riconoscibile e la gestione specializzata di una
                      piattaforma community-driven.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:col-span-7">
                {partnerBlocks.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <motion.div
                      key={p.title}
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{
                        duration: 0.7,
                        delay: i * 0.12,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="group relative border border-zinc-800 bg-zinc-950 p-8 transition-colors hover:border-[#FF5E1A]/40 hover:bg-zinc-900/60 md:p-10"
                      data-testid={`partner-${i}`}
                    >
                      <div className="flex items-start justify-between gap-6">
                        <div>
                          <p className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-[#FF5E1A]">
                            / {p.kicker}
                          </p>
                          <h3 className="mt-4 font-display text-2xl font-medium uppercase tracking-tight md:text-3xl">
                            {p.title}
                          </h3>
                          <p className="mt-4 max-w-xl leading-relaxed text-zinc-400">
                            {p.text}
                          </p>
                        </div>
                        <Icon
                          size={32}
                          strokeWidth={1.5}
                          className="shrink-0 text-zinc-600 transition-colors group-hover:text-[#FF5E1A]"
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* CONTATTI CTA */}
        <section id="contatti" className="relative px-6 py-24 md:py-32 lg:px-10">
          <div className="mx-auto max-w-[1400px]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative overflow-hidden border border-[#FF5E1A] bg-[#FF5E1A] p-8 text-zinc-950 md:p-16 lg:p-20"
            >
              <div className="grid gap-10 md:grid-cols-12 md:items-end">
                <div className="md:col-span-8">
                  <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-700">
                    / Contatti
                  </p>
                  <h2 className="mt-6 font-display text-4xl font-medium uppercase leading-[0.98] tracking-tight md:text-6xl lg:text-7xl">
                    Interessato allo
                    <br />
                    sviluppo di{" "}
                    <span className="italic font-normal">Alexquare</span>?
                  </h2>
                  <p className="mt-6 max-w-2xl text-lg leading-relaxed text-zinc-800">
                    Il progetto è aperto al confronto con sviluppatori
                    immobiliari, operatori gestionali, partner territoriali e
                    istituzioni interessate a nuovi modelli di spazio urbano per
                    giovani.
                  </p>
                </div>
                <div className="md:col-span-4 md:justify-self-end">
                  <a
                    href="mailto:info@alexquareurbanhub.it"
                    data-testid="contact-mailto-btn"
                    className="group inline-flex items-center gap-3 bg-zinc-950 px-8 py-5 text-xs font-bold uppercase tracking-[0.2em] text-[#FF5E1A] transition-colors hover:bg-zinc-800"
                  >
                    <Mail size={16} />
                    info@alexquareurbanhub.it
                    <ArrowUpRight
                      size={16}
                      className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* VIDEO MODAL */}
      <AnimatePresence>
        {videoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-zinc-950/95 backdrop-blur-md p-4 md:p-10"
            onClick={() => setVideoOpen(false)}
            data-testid="video-modal"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setVideoOpen(false);
              }}
              data-testid="video-close-btn"
              aria-label="Chiudi video"
              className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center border border-zinc-700 bg-zinc-950/80 text-zinc-50 transition-colors hover:border-[#FF5E1A] hover:text-[#FF5E1A] md:right-8 md:top-8"
            >
              <X size={22} />
            </button>
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl overflow-hidden border border-zinc-800 bg-black shadow-2xl"
            >
              <video
                src="https://customer-assets.emergentagent.com/job_indoor-quartiere/artifacts/tnbxyyip_AXQ%20VIDE0%202026.mp4"
                controls
                autoPlay
                playsInline
                className="block h-auto w-full"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FOOTER */}
      <footer
        data-testid="site-footer"
        className="border-t border-zinc-800 px-6 py-12 lg:px-10"
      >
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3">
                <div>
                  <p className="font-display text-sm font-semibold uppercase tracking-wide">
                    Alexquare Urban Hub
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Quartiere Indoor per le nuove generazioni
                  </p>
                </div>
              </div>
              <p className="mt-6 max-w-sm text-sm leading-relaxed text-zinc-400">
                Un nuovo modello di spazio urbano per le nuove generazioni.
                Progetto pilota a Tradate (VA), Italia.
              </p>
            </div>

            <div className="md:col-span-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                Naviga
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                <li>
                  <a
                    href="#progetto"
                    className="text-zinc-400 transition-colors hover:text-zinc-50"
                  >
                    Il progetto
                  </a>
                </li>
                <li>
                  <a
                    href="#concept"
                    className="text-zinc-400 transition-colors hover:text-zinc-50"
                  >
                    Il concept
                  </a>
                </li>
                <li>
                  <a
                    href="#pilota"
                    className="text-zinc-400 transition-colors hover:text-zinc-50"
                  >
                    Progetto pilota
                  </a>
                </li>
                <li>
                  <a
                    href="#sviluppo"
                    className="text-zinc-400 transition-colors hover:text-zinc-50"
                  >
                    Sviluppo
                  </a>
                </li>
              </ul>
            </div>

            <div className="md:col-span-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                Contatti
              </p>
              <a
                href="mailto:info@alexquareurbanhub.it"
                className="mt-4 block font-display text-xl text-zinc-50 transition-colors hover:text-[#FF5E1A]"
              >
                info@alexquareurbanhub.it
              </a>
              <p className="mt-2 text-sm text-zinc-500">Tradate · Varese · IT</p>
            </div>
          </div>

          <div className="mt-16 flex flex-col items-start justify-between gap-3 border-t border-zinc-800 pt-8 text-xs uppercase tracking-[0.2em] text-zinc-500 md:flex-row md:items-center">
            <p>© {new Date().getFullYear()} Alexquare Urban Hub</p>
            <p>Format urbano · Nuove generazioni</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
